import { referencedFields } from '@/utils/formula/formulaEngine';

/** One operand of a money formula and the code it is currently stated in. */
export interface OperandCurrency {
  token: string;
  code: string;
}

export interface OperandCurrencyReaders {
  /** Maps a formula token to the form field holding that operand's code. */
  currencyCodeNameOf?: (token: string) => string;
  /**
   * The offer's own money columns hold the id of the country the amount is
   * quoted in; a custom field holds the code itself. Both are read through
   * this so one total adding the two does not see the same currency as two.
   */
  normaliseCurrencyCode?: (raw: unknown) => string;
}

/**
 * What each operand of `formula` is stated in right now. An operand with no
 * code field on this form, or none picked yet, says nothing about the result's
 * currency and is left out rather than counted as a disagreement.
 */
export const operandCurrencies = (
  formula: string,
  values: Record<string, unknown>,
  { currencyCodeNameOf, normaliseCurrencyCode }: OperandCurrencyReaders
): OperandCurrency[] =>
  referencedFields(formula).flatMap((token) => {
    const codeName = currencyCodeNameOf?.(token);
    if (!codeName) return [];

    const raw = values[codeName];
    const code = normaliseCurrencyCode
      ? normaliseCurrencyCode(raw)
      : (raw as string);
    if (code === null || code === undefined || code === '') return [];

    return [{ token, code: String(code) }];
  });

/** The codes the operands agree on, as a set. */
const agreedCodes = (operands: OperandCurrency[]) =>
  new Set(operands.map((operand) => operand.code));

/**
 * The one code every operand is stated in, or '' where they disagree or none
 * states one. What a calculated money field's own code box follows.
 */
export const agreedCurrencyCode = (operands: OperandCurrency[]): string => {
  const agreed = agreedCodes(operands);
  return agreed.size === 1 ? [...agreed][0] : '';
};

/**
 * Whether the operands are stated in more than one currency. The amount is
 * then neither computable nor the recruiter's to type: a total carries one
 * currency, so the box is closed until they agree.
 */
export const operandCurrenciesDisagree = (
  operands: OperandCurrency[]
): boolean => agreedCodes(operands).size > 1;

// "A, B and C" -- the shape the refusal copy was written in. Two operands is
// the common case and reads "A and B", with no stray comma.
const listPhrase = (parts: string[]): string =>
  parts.length < 2
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/**
 * Said in the field while the recruiter is still in it. The save refuses this
 * too, but a refusal they only meet after hitting save is one they had no
 * chance to avoid -- and "currencies don't match" on its own does not say
 * which box to go and change, so each operand is named with its own code.
 *
 * '' when they agree, which is the same thing the field says then: nothing.
 */
export const currencyMismatchMessage = (
  operands: OperandCurrency[],
  operandLabelOf?: (token: string) => string
): string =>
  operandCurrenciesDisagree(operands)
    ? `Currencies don't match. ${listPhrase(
        operands.map(
          (operand) =>
            `${operandLabelOf?.(operand.token) || operand.token} is in ${
              operand.code
            }`
        )
      )}.`
    : '';
